import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { track } from '@eventbrite/legacy-infra.datalayer-library';
import {
  EventCardDate,
  EventCardImage,
  EventCardLocation,
  EventCardPrice,
  EventCardPromoted,
  EventCardTitle,
  EventCardTracking,
  VerticalEventCard,
  getEventCardLinkProps,
} from '@eventbrite/consumer-search.event-renderer';
import { EventCardLinkBase } from '@eventbrite/consumer-search.event-renderer/components/EventCardLink/EventCardLinkBase';
import { getImageProps } from 'next/image';

import styles from './RelatedEventCard.module.scss';
import { UrgencySignalBadge } from '@/components/UrgencySignals/UrgencySignalBadge';
import { useFormattedEventData } from './hooks/useFormattedEventData';
import { transformRelatedEventToTrackingEvent } from '@/utils/mappers';
import type { RelatedEvent } from '../types';
import type { AffiliateCodes } from '@/components/_shared/constants';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';

export interface RelatedEventCardProps {
  relatedEvent: RelatedEvent;
  affiliateCode?: AffiliateCodes;
  /**
   * Optional fan-out fired synchronously inside `handleCardClick`, before the
   * browser follows the anchor, so the parent (`RelatedEvents`) can emit the
   * PicoX `eb_event_page_opened` recommendation-click event without dropping
   * it on the about-to-unload page.
   */
  onCardClick?: (event: RelatedEvent) => void;
}

export const RelatedEventCard: React.FC<RelatedEventCardProps> = ({ relatedEvent, affiliateCode, onCardClick }) => {
  const { formatMessage } = useIntl();
  const formattedData = useFormattedEventData(relatedEvent);

  const titleClass = useMemo(() => {
    return formattedData?.urgencySignalBadge
      ? classNames(styles.title, { [styles.linegap]: !!formattedData.urgencySignalBadge })
      : styles.title;
  }, [formattedData?.urgencySignalBadge]);

  const handleCardClick = useCallback(() => {
    if (!formattedData?.url || formattedData.url === '#') {
      return;
    }

    track({
      eventName: LISTING_HEAP_IDS.LISTING_RELATED_EVENT_CLICK,
      eventData: {
        event_id: relatedEvent.id,
        event_name: relatedEvent.name,
      },
    });

    onCardClick?.(relatedEvent);
  }, [formattedData?.url, relatedEvent, onCardClick]);

  if (!relatedEvent || !formattedData) {
    return null;
  }

  const {
    props: { src },
  } = getImageProps({
    alt: '',
    width: 148,
    height: 74,
    src: formattedData.imageUrl || '',
  });

  const imageElement = <EventCardImage src={src} name={formattedData.name || formatMessage({ id: 'Event image' })} />;

  const linkProps = getEventCardLinkProps({
    event: {
      url: formattedData.url,
      name: formattedData.name ?? '',
      isPromoted: formattedData.isPromoted,
    },
    affCode: affiliateCode ?? '',
    onClick: handleCardClick,
    isTargetBlank: true,
    label: `${formatMessage({ id: 'Open event' })}: ${formattedData.name || formatMessage({ id: 'Unnamed event' })}`,
  });

  // getEventCardLinkProps always passes `{ aff }` to formatUrl, which appends `?aff=` even
  // for the empty string. When no affiliate code is set, drop the trailing empty param so
  // crawlers see the canonical URL.
  const href = affiliateCode ? linkProps.href : linkProps.href.replace(/\?aff=$/, '');

  const eventForTracking = transformRelatedEventToTrackingEvent(relatedEvent);

  const cardContent = (
    <div className={styles.grid}>
      {formattedData.urgencySignalBadge && (
        <div className={styles.signalBadgeWrapper}>
          <UrgencySignalBadge badge={formattedData.urgencySignalBadge} />
        </div>
      )}

      {formattedData.name && (
        <div className={styles.titleWrapper}>
          <EventCardTitle title={formattedData.name} className={titleClass} />
        </div>
      )}

      {formattedData.formattedEventDateTimeString && (
        <div className={styles.dateWrapper}>
          <EventCardDate formattedDateString={formattedData.formattedEventDateTimeString} className={styles.date} />
        </div>
      )}

      <div className={styles.locationWrapper}>
        <EventCardLocation formattedLocationString={formattedData.locationString} className={styles.location} />
      </div>

      <div className={styles.priceWrapper}>
        <EventCardPrice
          isFree={formattedData.isFree}
          formattedPriceString={formattedData.priceString}
          className={styles.price}
        />
      </div>

      {formattedData.isPromoted && (
        <div className={styles.promotedWrapper} data-testid="promoted-event-card">
          <EventCardPromoted isPromoted={formattedData.isPromoted} className={styles.promoted} />
        </div>
      )}

      <div className={styles.imageWrapper}>{imageElement}</div>
    </div>
  );

  return (
    <div data-testid="related-event-card" className={styles.wrapper}>
      <VerticalEventCard isRaised={false}>
        <EventCardTracking event={eventForTracking}>
          <EventCardLinkBase {...linkProps} href={href}>
            {cardContent}
          </EventCardLinkBase>
        </EventCardTracking>
      </VerticalEventCard>
    </div>
  );
};
